import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { DEFAULT_TOP_RANKING_LIMIT } from "../../utils/kpiRankingMetric";
import {
  parseCasinoTopFiveResponse,
  type CasinoTopFiveData,
} from "./normalizeCasinoRankingRows";
import { buildCasinoKpiMoneyTypeParam } from "./casinoKpiFilters";

export type CasinoTopRankingsFilters = {
  start_date: string;
  end_date: string;
  /** Number of ranked rows to return (BE: 1–50). Defaults to 10. */
  limit?: number;
  money_type?: string[];
};

const fetchCasinoTopRankings = async (
  route: string,
  filters: CasinoTopRankingsFilters,
): Promise<CasinoTopFiveData> => {
  const instance = getPrivateInstance();
  const {
    start_date,
    end_date,
    limit = DEFAULT_TOP_RANKING_LIMIT,
    money_type,
  } = filters;
  const response = await instance.get(route, {
    params: {
      start_date,
      end_date,
      limit,
      ...buildCasinoKpiMoneyTypeParam(money_type),
    },
  });
  return parseCasinoTopFiveResponse(response?.data);
};

const useCasinoTopRankings = (
  route: string,
  queryKey: string,
  filters: CasinoTopRankingsFilters,
): UseQueryResult<CasinoTopFiveData, Error> => {
  const {
    start_date,
    end_date,
    limit = DEFAULT_TOP_RANKING_LIMIT,
    money_type,
  } = filters;

  return useQuery<CasinoTopFiveData, Error>({
    queryKey: [
      queryKey,
      start_date,
      end_date,
      limit,
      money_type?.join(",") ?? "",
    ],
    queryFn: () => fetchCasinoTopRankings(route, filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};

/** Top providers ranked across every metric (turnover, ggr, etc.). */
export const useGetCasinoTopProviders = (filters: CasinoTopRankingsFilters) =>
  useCasinoTopRankings(
    API_ROUTES.CASINO_TOP_PROVIDERS,
    "CASINO_TOP_PROVIDERS",
    filters,
  );

/** Top games ranked across every metric (turnover, ggr, etc.). */
export const useGetCasinoTopGames = (filters: CasinoTopRankingsFilters) =>
  useCasinoTopRankings(
    API_ROUTES.CASINO_TOP_GAMES,
    "CASINO_TOP_GAMES",
    filters,
  );
